import { useState, useEffect, useCallback, useMemo } from 'react'
import { BlogMetadataRecord } from 'api/clients/BlogMetadataRecordsClient'

export interface UseBlogMetadataCategoriesOptions {
  initialData?: BlogMetadataRecord
  onCategoriesUpdate?: (categories: string[]) => void
}

export interface BlogMetadataCategoriesState {
  selectedCategories: string[]
}

export interface BlogMetadataCategoriesHandlers {
  handleCategoriesChange: (newCategories: string[]) => void
  handleCategoryDropdownClose: (selectedCats: string[], updatedAvailable: string[]) => void
}

export interface UseBlogMetadataCategoriesReturn {
  categoriesState: BlogMetadataCategoriesState
  handlers: BlogMetadataCategoriesHandlers
}

/**
 * Hook to manage BlogMetadata categories
 * Generic hook that works with both TagInput and CategoryDropdown
 */
export const useBlogMetadataCategories = ({
  initialData,
  onCategoriesUpdate,
}: UseBlogMetadataCategoriesOptions): UseBlogMetadataCategoriesReturn => {
  // Memoize initialData values to prevent unnecessary re-renders
  const initialCategories = useMemo(() => initialData?.categories ?? [], [initialData?.categories])
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategories)

  // Sync state with initialData changes (only on mount)
  useEffect(() => {
    setSelectedCategories(initialCategories)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Intentionally empty dependency array to prevent sync on every initialData change

  // Handlers
  const handleCategoriesChange = useCallback(
    (newCategories: string[]) => {
      setSelectedCategories(newCategories)
      onCategoriesUpdate?.(newCategories)
    },
    [onCategoriesUpdate],
  )

  const handleCategoryDropdownClose = useCallback(
    (selectedCats: string[], updatedAvailable: string[]) => {
      setSelectedCategories(selectedCats)
      onCategoriesUpdate?.(selectedCats)
      // Note: updatedAvailable contains newly added categories
      // The parent (composition hook) will handle persistence
    },
    [onCategoriesUpdate],
  )

  return {
    categoriesState: {
      selectedCategories,
    },
    handlers: {
      handleCategoriesChange,
      handleCategoryDropdownClose,
    },
  }
}
